import React, { useState, useEffect } from 'react';
import { startAITutorSession, endAITutorSession } from '../apiClient';
import { useJWTAuth } from '../JWTAuthContext';

interface AITutorSession {
  session_id: number;
  status: 'active' | 'ended';
  start_time: string;
  end_time?: string;
  points_spent: number;
  minutes_used: number;
}

const AITutorInterface: React.FC = () => {
  const { user } = useJWTAuth();
  const [currentSession, setCurrentSession] = useState<AITutorSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionTimer, setSessionTimer] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [userPoints, setUserPoints] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate cost and available minutes
  const pointsPerMinute = 120;
  const availableMinutes = Math.floor(userPoints / pointsPerMinute);

  useEffect(() => {
    // Fetch user points on component mount
    fetchUserPoints();
    
    // Check for existing active session
    checkActiveSession();
    
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserPoints = async () => {
    try {
      // This would typically come from the point summary API
      // For now, we'll fetch it when needed
      setUserPoints(0); // Placeholder - implement actual fetch
    } catch (error) {
      console.error('Failed to fetch user points:', error);
    }
  };

  const checkActiveSession = () => {
    // Check if there's an active session in localStorage
    const storedSession = localStorage.getItem('ai_tutor_session');
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        if (session.status === 'active') {
          setCurrentSession(session);
          const startTime = new Date(session.start_time).getTime();
          const currentTime = new Date().getTime();
          const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
          setSessionTimer(elapsedSeconds);
          startTimer();
        }
      } catch (error) {
        console.error('Error parsing stored session:', error);
        localStorage.removeItem('ai_tutor_session');
      }
    }
  };

  const startTimer = () => {
    if (timerInterval) clearInterval(timerInterval);
    
    const interval = setInterval(() => {
      setSessionTimer(prev => prev + 1);
    }, 1000);
    
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const handleStartSession = async () => {
    if (userPoints < pointsPerMinute) {
      setError('Insufficient points to start AI Tutor session. You need at least 60 points.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await startAITutorSession({});
      
      if (response && response.success && response.data) {
        const sessionData: AITutorSession = {
          session_id: response.data.session_id,
          status: 'active',
          start_time: new Date().toISOString(),
          points_spent: response.data.points_spent,
          minutes_used: 0
        };
        
        setCurrentSession(sessionData);
        setUserPoints(response.data.remaining_points);
        setSessionTimer(0);
        startTimer();
        
        // Store session in localStorage
        localStorage.setItem('ai_tutor_session', JSON.stringify(sessionData));
      } else {
        setError('Failed to start AI Tutor session');
      }
    } catch (error) {
      console.error('Failed to start AI Tutor session:', error);
      setError('Error starting AI Tutor session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!currentSession) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await endAITutorSession({
        session_id: currentSession.session_id
      });
      
      if (response && response.success && response.data) {
        stopTimer();
        setCurrentSession(null);
        setUserPoints(response.data.remaining_points);
        setSessionTimer(0);
        
        // Remove session from localStorage
        localStorage.removeItem('ai_tutor_session');
        
        // Show success message with session summary
        alert(`Session ended successfully!\nMinutes used: ${response.data.minutes_used}\nPoints spent: ${response.data.points_spent}\nRemaining points: ${response.data.remaining_points}`);
      } else {
        setError('Failed to end AI Tutor session');
      }
    } catch (error) {
      console.error('Failed to end AI Tutor session:', error);
      setError('Error ending AI Tutor session');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getSessionCost = (): number => {
    const minutes = Math.ceil(sessionTimer / 60);
    return minutes * pointsPerMinute;
  };

  return (
    <div className="card border-0 shadow-sm">
      <div 
        className="card-header bg-white border-0" 
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        {!isExpanded ? (
          <div className="d-flex align-items-center">
            <div 
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #17a2b8, #138496)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                flexShrink: 0
              }}
            >
              <i className="fas fa-robot" style={{ fontSize: '24px', color: 'white' }}></i>
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>AI Tutor Session</h5>
            </div>
            {currentSession && (
              <>
                <span className="badge bg-primary me-2" style={{ fontSize: '11px', padding: '4px 10px' }}>
                  <i className="fas fa-clock me-1"></i>
                  {formatTime(sessionTimer)}
                </span>
                <span className="badge bg-success me-2" style={{ fontSize: '11px', padding: '4px 10px' }}>Active</span>
              </>
            )}
            <div 
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'rgba(23, 162, 184, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <i className="fas fa-chevron-down" style={{ fontSize: '11px', color: '#17a2b8' }}></i>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <div 
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #17a2b8, #138496)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                flexShrink: 0
              }}
            >
              <i className="fas fa-robot" style={{ fontSize: '24px', color: 'white' }}></i>
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>AI Tutor Session</h5>
            </div>
            {currentSession && (
              <span className="badge bg-success me-2" style={{ fontSize: '11px', padding: '4px 10px' }}>Active</span>
            )}
            <div 
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'rgba(23, 162, 184, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-chevron-up" style={{ color: '#17a2b8', fontSize: '11px' }}></i>
            </div>
          </div>
        )}
      </div>

      {isExpanded && (
      <div className="card-body animate__animated animate__fadeIn">
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Session Status */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <span className="fw-bold me-2">Status:</span>
              {currentSession ? (
                <span className="badge bg-success">
                  <i className="fas fa-circle me-1"></i>
                  Active Session
                </span>
              ) : (
                <span className="badge bg-secondary">
                  <i className="fas fa-circle me-1"></i>
                  No Active Session
                </span>
              )}
            </div>
            
            {currentSession && (
              <div className="d-flex align-items-center mb-2">
                <span className="fw-bold me-2">Session Timer:</span>
                <span className="badge bg-primary fs-6">
                  {formatTime(sessionTimer)}
                </span>
              </div>
            )}
          </div>
          
          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <span className="fw-bold me-2">Available Points:</span>
              <span className="text-primary fw-bold">{userPoints.toLocaleString()}</span>
            </div>
            
            <div className="d-flex align-items-center mb-2">
              <span className="fw-bold me-2">Available Minutes:</span>
              <span className="text-success fw-bold">{availableMinutes}</span>
            </div>
          </div>
        </div>

        {/* Pricing Information */}
        <div className="alert alert-info">
          <h6><i className="fas fa-info-circle me-2"></i>AI Tutor Pricing</h6>
          <p className="mb-2">
            <strong>Cost:</strong> 120 points per minute
          </p>
          {currentSession && (
            <p className="mb-0">
              <strong>Current Session Cost:</strong> {getSessionCost()} points
            </p>
          )}
        </div>

        {/* Coming Soon Message */}
        <div className="alert alert-info text-center">
          <i className="fas fa-robot mb-3" style={{ fontSize: '3rem', opacity: '0.6' }}></i>
          <h5 className="mb-3">AI Tutor Coming Soon!</h5>
          <p className="mb-0">
            Our advanced AI tutoring system is currently being developed. 
            <br />
            Stay tuned for personalized learning experiences!
          </p>
        </div>

        {/* Session Instructions */}
        {currentSession && (
          <div className="mt-4">
            <div className="alert alert-warning">
              <h6><i className="fas fa-clock me-2"></i>Session Active</h6>
              <p className="mb-2">
                Your AI Tutor session is now active. You can now access personalized tutoring features.
              </p>
              <p className="mb-0">
                <strong>Remember:</strong> You will be charged 120 points for each minute used. 
                Make sure to end your session when you're done to save points.
              </p>
            </div>
          </div>
        )}

        {/* Help Information */}
        {!currentSession && (
          <div className="mt-4">
            <h6>How AI Tutor Works:</h6>
            <ul className="list-unstyled">
              <li><i className="fas fa-check text-success me-2"></i>Start a session to access personalized AI tutoring</li>
              <li><i className="fas fa-check text-success me-2"></i>Get instant help with questions and explanations</li>
              <li><i className="fas fa-check text-success me-2"></i>Receive customized study recommendations</li>
              <li><i className="fas fa-check text-success me-2"></i>Track your learning progress in real-time</li>
            </ul>
            
            {userPoints < pointsPerMinute && (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                You need at least 120 points to start an AI Tutor session. 
                Complete quizzes to earn more points!
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default AITutorInterface;